import * as React from 'react';
import {ValidationException} from '@practicegenius/http-clients-js/src/exceptions';
import {DeepNonNullable} from '@practicegenius/pg-util/src/TypescriptUtils';
import {IMT} from '@practicegenius/pg-util/src/StringUtils';
import {objectKeysToCamelCaseDeep} from '@practicegenius/pg-util/src/ObjectUtils';
import {
  AccountRegistration,
  SocialOauthError,
  SocialPlatform,
} from '../data/types';
import lang from '../lang';
import * as registrationMachine from '../machines/accountRegistration';
import {Context, Event} from '../machines/machineTypes';
import {UseMachineType} from '../machines/useAccountRegistrationMachineType';
import {calculateProgress} from './utils';

type Props = {
  registrationMachine: UseMachineType;
  pgCoreUrl: string;
};

type SocialLoginProps = {
  onAuthSuccess: (payload: AccountRegistration) => unknown;
  onAuthError: (payload: SocialOauthError) => unknown;
  label: string;
  targetUrl: string;
};

type ScreenProps = Props & {
  lang: typeof lang.registrationMethods;
  progress: number;
  instructions: string;
  socialLogin: Partial<{
    facebook: SocialLoginProps;
    google: SocialLoginProps;
    apple: SocialLoginProps;
  }>;
  continueWithEmailButtonProps: {
    onClick: () => unknown;
  };
};

export type RegistrationMethodsScreenType = React.ComponentType<ScreenProps>;

class SocialAuthFatalError extends Error {
  payload: unknown;
  constructor(payload: unknown) {
    super();
    this.payload = payload;
  }
}

const getRegistrationMethod = (
  cardNumber: string | null,
  inviteCode: string | null,
  businessCode: string | null
) => {
  if (businessCode) {
    return lang.registrationMethods.instructions.registrationMethod
      .businessCode;
  }

  if (inviteCode) {
    return lang.registrationMethods.instructions.registrationMethod.inviteCode;
  }

  return /\d{16}/.test(cardNumber ?? '')
    ? lang.registrationMethods.instructions.registrationMethod.validCardNumber
    : lang.registrationMethods.instructions.registrationMethod
        .invalidCardNumber;
};

const getDescription = (registrationContext: Context) => {
  const registrationMethod = getRegistrationMethod(
    registrationContext.cardNumber,
    registrationContext.inviteCode,
    registrationContext.businessCode
  );

  const readyToRegisterText = registrationContext.linkedPractice
    ? IMT(
        lang.registrationMethods.instructions.readyToRegisterText
          .linkedPractice,
        {
          linkedPracticeName: registrationContext.linkedPractice?.name,
        }
      )
    : lang.registrationMethods.instructions.readyToRegisterText.default; // I'm not confident this case actually exists.

  return IMT(lang.registrationMethods.instructions.base, {
    registrationMethod,
    readyToRegisterText,
  });
};

export default function RegistrationMethods(
  props: Props & {children: RegistrationMethodsScreenType}
) {
  const [
    {
      context,
      context: {
        navigator: {location},
      },
    },
    send,
  ] = props.registrationMachine;

  const socialRegistrationErrorHandler =
    (platform: SocialPlatform) => (payload: SocialOauthError) => {
      switch (payload.code) {
        case 'account_registered':
          return send({
            type: Event.SKIP_TO_ALREADY_REGISTERED,
            message: payload.message,
            hint: payload.hint,
          });
        case 'social_registered':
          return send({
            type: Event.SKIP_TO_SOCIAL_ALREADY_REGISTERED,
            message: payload.message,
            hint: payload.hint ?? {},
          });
        default:
          return send({
            type: Event.UNHANDLED_ERROR,
            message: IMT(lang.general.registrationError.message, {
              detailedError: IMT(
                lang.general.registrationError.failedConnectingSocialPlatform,
                {
                  platform,
                }
              ),
            }),
            error: new SocialAuthFatalError(payload),
          });
      }
    };

  const socialRegistrationSuccessHandler =
    (platform: SocialPlatform) =>
    async (
      payload: // AccountRegistration object is returned from the websocket connection on the web.
      | AccountRegistration
        // When registering via the mobile app, we only get the provider token.
        | {providerToken: string; extraData: Record<string, unknown>}
    ) => {
      send({
        type: Event.UPDATE_REGISTRATION_METHOD,
        payload: {
          type: 'social',
          provider: platform,
        },
      });

      let user: AccountRegistration['user'];
      let registration: AccountRegistration['registration'];
      if ('providerToken' in payload) {
        try {
          const data = (await context.apiClient._unsafe
            .post(
              `/global_users/registration/${platform}/token?include=contacts`,
              {
                token: context.token,
                provider_token: payload.providerToken,
                ...(payload.extraData ?? {}),
                business_code: context.linkedPractice?.businessCode,
              }
            )
            .then(res =>
              objectKeysToCamelCaseDeep(res.data.item)
            )) as DeepNonNullable<AccountRegistration>;
          user = data.user;
          registration = data.registration;
        } catch (e) {
          if (e instanceof ValidationException) {
            return socialRegistrationErrorHandler(platform)(
              e.data as SocialOauthError
            );
          }

          throw e;
        }
      } else {
        user = payload.user;
        registration = payload.registration;
      }

      if (registration.completed && registration.requires.length === 0) {
        return send({
          type: registrationMachine.RegistrationEvent.LOGIN_VIA_SOCIAL,
          socialProvider: platform,
        });
      }

      if (user.firstname && user.lastname) {
        send({
          type: registrationMachine.RegistrationEvent.UPDATE_REGISTRATION,
          payload: {
            account: {
              firstname: user.firstname,
              lastname: user.lastname,
            },
            linkedViaSocial: true,
            socialProvider: platform,
            token: registration.token,
          },
        });
      } else if (context.cardNumber) {
        send({
          type: registrationMachine.RegistrationEvent.UPDATE_REGISTRATION,
          payload: {
            linkedViaSocial: true,
            socialProvider: platform,
            token: registration.token,
          },
        });
      } else {
        send({
          type: registrationMachine.RegistrationEvent.NEXT,
          payload: {
            completed: registration.completed,
            requires: registration.requires,
            draftAccount: user,
          },
        });
      }
    };

  const makeTargetUrl = (method: SocialPlatform) =>
    `${props.pgCoreUrl}/global_users/registration/${method}?business_code=${
      context.linkedPractice?.businessCode || ''
    }&registration_token=${context.token || ''}`;

  const socialLogin = !context.allowSocialRegistration
    ? {}
    : {
        facebook: {
          onAuthSuccess: socialRegistrationSuccessHandler('facebook'),
          onAuthError: socialRegistrationErrorHandler('facebook'),
          label: lang.registrationMethods.socialLogin.facebook.label,
          targetUrl: makeTargetUrl('facebook'),
        },
        google: {
          onAuthSuccess: socialRegistrationSuccessHandler('google'),
          onAuthError: socialRegistrationErrorHandler('google'),
          label: lang.registrationMethods.socialLogin.google.label,
          targetUrl: makeTargetUrl('google'),
        },
        apple: {
          onAuthSuccess: socialRegistrationSuccessHandler('apple'),
          onAuthError: socialRegistrationErrorHandler('apple'),
          label: lang.registrationMethods.socialLogin.apple.label,
          targetUrl: makeTargetUrl('apple'),
        },
      };

  const progress = calculateProgress({
    draftAccount: context.account,
    location,
  });

  const instructions = getDescription(context);

  const renderProps: ScreenProps = {
    ...props,
    lang: lang.registrationMethods,
    progress,
    instructions,
    socialLogin,
    continueWithEmailButtonProps: {
      onClick: () => {
        send({
          type: registrationMachine.RegistrationEvent
            .UPDATE_REGISTRATION_METHOD,
          payload: {
            type: 'email',
          },
        });
        return send(registrationMachine.RegistrationEvent.NEXT);
      },
    },
  };

  const Screen = props.children;
  return <Screen {...renderProps} />;
}
